import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Activity, Briefcase, CalendarDays, Download, FileText, Factory, Images, Inbox, Loader2, LogOut, RefreshCw, Search } from "lucide-react";
import { API, useAuth } from "@/context/AuthContext";
import GalleryManager from "@/components/admin/GalleryManager";
import WebsiteTracking from "@/components/admin/WebsiteTracking";
import JobsManager from "@/components/admin/JobsManager";
import MachinesManager from "@/components/admin/MachinesManager";
import HolidaysManager from "@/components/admin/HolidaysManager";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_LABELS = { neu: "Neu", in_bearbeitung: "In Bearbeitung", angebot_erstellt: "Angebot erstellt", erledigt: "Erledigt" };
const STATUS_COLORS = {
  neu: "text-[var(--ci-blue)] border-[var(--ci-blue)]/30 bg-[var(--ci-blue)]/5",
  in_bearbeitung: "text-[#B45309] border-[#F59E0B]/40 bg-[#F59E0B]/10",
  angebot_erstellt: "text-[#7C3AED] border-[#8B5CF6]/40 bg-[#8B5CF6]/10",
  erledigt: "text-[#047857] border-[#10B981]/40 bg-[#10B981]/10",
};

const fmtDate = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("anfragen");
  const [anfragen, setAnfragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("alle");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/anfragen`, { withCredentials: true });
      setAnfragen(data);
    } catch {
      toast.error("Anfragen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    gesamt: anfragen.length,
    neu: anfragen.filter((a) => a.status === "neu").length,
    aktiv: anfragen.filter((a) => a.status === "in_bearbeitung").length,
    erledigt: anfragen.filter((a) => a.status === "erledigt").length,
  }), [anfragen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return anfragen.filter((a) => {
      if (statusFilter !== "alle" && a.status !== statusFilter) return false;
      if (!q) return true;
      return [a.firma, a.ansprechpartner, a.email, a.bearbeitungsart, a.material, a.id]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [anfragen, query, statusFilter]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/anfragen/${id}`, { status }, { withCredentials: true });
      setAnfragen((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      setSelected((prev) => (prev?.id === id ? { ...prev, status } : prev));
      toast.success("Status aktualisiert");
    } catch {
      toast.error("Status konnte nicht gespeichert werden");
    }
  };

  const download = async (file) => {
    setDownloading(file.storage_path);
    try {
      const res = await axios.get(`${API}/dateien/${file.storage_path}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Datei konnte nicht heruntergeladen werden");
    } finally {
      setDownloading(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F7F9FA] text-[var(--ci-grey)] flex" data-testid="admin-dashboard">
      <aside className="w-64 shrink-0 border-r border-[#E3E8EC] bg-white hidden md:flex flex-col">
        <div className="px-6 h-[76px] flex items-center border-b border-[#E3E8EC]">
          <img src="/assets/hamm-logo.png" alt="Hamm Entwicklung + Produktion GmbH" className="h-10 w-auto" />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {[
            ["anfragen", "Fertigungsanfragen", Inbox, "admin-nav-inbox"],
            ["galerie", "Projekt-Galerie", Images, "admin-nav-gallery"],
            ["tracking", "Website-Tracking", Activity, "admin-nav-tracking"],
            ["jobs", "Karriere / Jobs", Briefcase, "admin-nav-jobs"],
            ["machines", "Maschinenpark", Factory, "admin-nav-machines"],
            ["holidays", "Feiertags-Events", CalendarDays, "admin-nav-holidays"],
          ].map(([key, label, Icon, tid]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={tid}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-l-2 transition-colors duration-200 ${tab === key ? "bg-[var(--ci-blue-light)] border-[var(--ci-blue)]" : "border-transparent hover:bg-[#F7F9FA]"}`}>
              <Icon size={16} className={tab === key ? "text-[var(--ci-blue)]" : "text-[var(--ci-grey)]"} />
              <span className={`text-sm ${tab === key ? "font-medium text-[var(--ci-grey)]" : "text-[var(--ci-grey)]"}`}>{label}</span>
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E3E8EC]">
          <div className="px-4 py-2 font-tech text-[10px] tracking-[0.15em] text-[var(--ci-grey)] truncate">{user?.email}</div>
          <button onClick={handleLogout} data-testid="admin-logout-btn"
            className="mt-1 w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] hover:bg-[#F7F9FA] transition-colors">
            <LogOut size={15} /> Abmelden
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="h-[76px] bg-white border-b border-[#E3E8EC] flex items-center justify-between px-6 lg:px-10">
          <h1 className="text-lg font-bold tracking-tight text-[var(--ci-grey)]">{tab === "anfragen" ? "Anfragen-Übersicht" : tab === "galerie" ? "Projekt-Galerie" : tab === "tracking" ? "Website-Tracking" : tab === "machines" ? "Maschinenpark" : tab === "holidays" ? "Feiertags-Events" : "Karriere / Stellenverwaltung"}</h1>
          <div className="flex items-center gap-3">
            {tab === "anfragen" && (
              <button onClick={() => { setLoading(true); load(); }} data-testid="admin-refresh-btn"
                className="flex items-center gap-2 px-5 h-9 rounded-full border border-[#E3E8EC] text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] hover:border-[var(--ci-turquoise)] transition-colors font-tech text-[10px] tracking-[0.2em] uppercase">
                <RefreshCw size={13} /> Aktualisieren
              </button>
            )}
            <button onClick={() => setTab(tab === "anfragen" ? "galerie" : "anfragen")} data-testid="admin-tab-switch-mobile"
              className="md:hidden flex items-center gap-2 px-4 h-9 rounded-full border border-[#E3E8EC] text-[var(--ci-grey)] font-tech text-[10px] tracking-[0.2em] uppercase">
              {tab === "anfragen" ? <Images size={13} /> : <Inbox size={13} />}
            </button>
            <button onClick={handleLogout} data-testid="admin-logout-btn-mobile"
              className="md:hidden flex items-center gap-2 px-4 h-9 rounded-full border border-[#E3E8EC] text-[var(--ci-grey)] font-tech text-[10px] tracking-[0.2em] uppercase">
              <LogOut size={13} />
            </button>
          </div>
        </header>

        {tab === "anfragen" ? (
        <div className="p-6 lg:p-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-[#E3E8EC] rounded-2xl overflow-hidden" data-testid="admin-stats">
            {[["Gesamt", stats.gesamt], ["Neu", stats.neu], ["In Bearbeitung", stats.aktiv], ["Erledigt", stats.erledigt]].map(([label, val]) => (
              <div key={label} className="border-b border-r border-[#E3E8EC] px-7 py-7 bg-white">
                <div className="font-tech text-2xl text-[var(--ci-grey)]" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>{val}</div>
                <div className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)] mt-1.5">{label}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="admin-filter-bar">
            <div className="relative flex-1 max-w-md">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ci-grey)]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Suche: Firma, Ansprechpartner, E-Mail, Vorgang …"
                data-testid="admin-search-input"
                className="w-full h-11 pl-11 pr-4 rounded-xl border border-[#D9E0E6] bg-white text-sm text-[var(--ci-grey)] placeholder:text-[#B9C2CA] focus:outline-none focus:ring-2 focus:ring-[var(--ci-turquoise)] focus:border-[var(--ci-turquoise)]"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-56 h-11 bg-white border-[#D9E0E6] text-[var(--ci-grey)] rounded-xl focus:ring-2 focus:ring-[var(--ci-turquoise)]" data-testid="admin-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E3E8EC] text-[var(--ci-grey)]">
                <SelectItem value="alle" className="focus:bg-[var(--ci-blue-light)] focus:text-[var(--ci-grey)]" data-testid="status-filter-alle">Alle Status</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k} className="focus:bg-[var(--ci-blue-light)] focus:text-[var(--ci-grey)]" data-testid={`status-filter-${k}`}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(query.trim() || statusFilter !== "alle") && (
              <span className="font-tech text-[10px] tracking-[0.15em] uppercase text-[var(--ci-grey)]" data-testid="admin-filter-count">
                {filtered.length} von {anfragen.length}
              </span>
            )}
          </div>

          <div className="mt-4 border border-[#E3E8EC] bg-white rounded-2xl overflow-hidden overflow-x-auto shadow-[0_4px_40px_rgba(17,24,39,0.04)]">
            {loading ? (
              <div className="p-16 flex items-center justify-center gap-3 text-[var(--ci-grey)]" data-testid="inquiries-loading">
                <Loader2 size={18} className="animate-spin" /> Lade Anfragen…
              </div>
            ) : anfragen.length === 0 ? (
              <div className="p-16 text-center" data-testid="inquiries-empty">
                <Inbox size={32} className="mx-auto text-[#C3CCD4]" />
                <p className="mt-4 text-sm text-[var(--ci-grey)]">Noch keine Anfragen eingegangen.</p>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="inquiries-table">
                <thead>
                  <tr className="border-b border-[#E3E8EC] bg-[#F7F9FA]">
                    {["Eingang", "Firma", "Bearbeitungsart", "Material", "Dateien", "Status"].map((h) => (
                      <th key={h} className="text-left px-6 py-4 font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)] font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-sm text-[var(--ci-grey)]" data-testid="inquiries-filter-empty">
                        Keine Anfragen für diese Filtereinstellung.
                      </td>
                    </tr>
                  )}
                  {filtered.map((a) => (
                    <tr key={a.id} onClick={() => setSelected(a)} data-testid={`inquiry-row-${a.id}`}
                      className="border-b border-[#EEF1F4] last:border-0 hover:bg-[#F7F9FA] cursor-pointer transition-colors">
                      <td className="px-6 py-4 font-tech text-xs text-[var(--ci-grey)] whitespace-nowrap">{fmtDate(a.created_at)}</td>
                      <td className="px-6 py-4 font-medium text-[var(--ci-grey)]">{a.firma}</td>
                      <td className="px-6 py-4 text-[var(--ci-grey)]">{a.bearbeitungsart}</td>
                      <td className="px-6 py-4 text-[var(--ci-grey)] whitespace-nowrap">{a.materialstaerke}</td>
                      <td className="px-6 py-4 font-tech text-xs text-[var(--ci-grey)]">{a.dateien?.length || 0}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-2.5 py-1 border rounded-full font-tech text-[10px] tracking-[0.12em] uppercase whitespace-nowrap ${STATUS_COLORS[a.status] || ""}`}>
                          {STATUS_LABELS[a.status] || a.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        ) : tab === "galerie" ? (
        <div className="p-6 lg:p-10" data-testid="gallery-tab-panel">
          <GalleryManager />
        </div>
        ) : tab === "tracking" ? (
        <div className="p-6 lg:p-10" data-testid="tracking-tab-panel">
          <WebsiteTracking />
        </div>
        ) : tab === "machines" ? (
        <div className="p-6 lg:p-10" data-testid="machines-tab-panel">
          <MachinesManager />
        </div>
        ) : tab === "holidays" ? (
        <div className="p-6 lg:p-10" data-testid="holidays-tab-panel">
          <HolidaysManager />
        </div>
        ) : (
        <div className="p-6 lg:p-10" data-testid="jobs-tab-panel">
          <JobsManager />
        </div>
        )}
      </main>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="bg-white border-[#E3E8EC] text-[var(--ci-grey)] max-w-2xl rounded-2xl" data-testid="inquiry-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold tracking-tight text-[var(--ci-grey)]">
                  {selected.firma}
                  <span className="block font-tech text-[10px] tracking-[0.25em] uppercase text-[var(--ci-blue)] mt-1">
                    Vorgang HEP-{selected.id.slice(0, 8).toUpperCase()} · {fmtDate(selected.created_at)}
                  </span>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4 mt-2 text-sm">
                {[
                  ["Ansprechpartner", selected.ansprechpartner],
                  ["E-Mail", selected.email],
                  ["Telefon", selected.telefon || "—"],
                  ["Losgröße", selected.losgroesse || "—"],
                  ["Bearbeitungsart", selected.bearbeitungsart],
                  ["Material", selected.material],
                  ["Materialstärke", selected.materialstaerke],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)]">{label}</div>
                    <div className="text-[var(--ci-grey)] mt-1 break-words">{value}</div>
                  </div>
                ))}
                <div>
                  <div className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)]">Status</div>
                  <Select value={selected.status} onValueChange={(v) => setStatus(selected.id, v)}>
                    <SelectTrigger className="mt-1 bg-white border-[#D9E0E6] text-[var(--ci-grey)] rounded-xl h-10 focus:ring-2 focus:ring-[var(--ci-turquoise)]" data-testid="inquiry-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E3E8EC] text-[var(--ci-grey)]">
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k} className="focus:bg-[var(--ci-blue-light)] focus:text-[var(--ci-grey)]">{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {selected.nachricht && (
                <div className="mt-5">
                  <div className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)]">Nachricht</div>
                  <p className="mt-1 text-sm text-[var(--ci-grey)] leading-relaxed border border-[#E3E8EC] bg-[#F7F9FA] rounded-xl p-4">{selected.nachricht}</p>
                </div>
              )}
              <div className="mt-5">
                <div className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)]">Anhänge ({selected.dateien?.length || 0})</div>
                {selected.dateien?.length ? (
                  <ul className="mt-2 space-y-2">
                    {selected.dateien.map((f, i) => (
                      <li key={f.storage_path} className="flex items-center justify-between border border-[#E3E8EC] bg-[#F7F9FA] rounded-xl px-4 py-2.5">
                        <span className="flex items-center gap-3 font-tech text-xs text-[var(--ci-grey)] truncate">
                          <FileText size={14} className="text-[var(--ci-blue)] shrink-0" /> {f.original_filename}
                          <span className="text-[var(--ci-grey)]">({(f.size / 1024).toFixed(0)} KB)</span>
                        </span>
                        <button onClick={() => download(f)} disabled={downloading === f.storage_path} data-testid={`file-download-${i}`}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E3E8EC] hover:border-[var(--ci-turquoise)] text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] transition-colors font-tech text-[10px] tracking-[0.15em] uppercase disabled:opacity-50">
                          {downloading === f.storage_path ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Laden
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-[var(--ci-grey)]">Keine Dateien angehängt.</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
